import { useNavigate } from 'react-router-dom'
import { useSession } from '../lib/session'
import { useTheme } from '../lib/theme'
import { Shell } from '../components/Shell'
import Security from '../components/Security'
import Mfa from '../components/Mfa'
import Devices from '../components/Devices'
import AccountDelete from '../components/AccountDelete'
import DataExport from '../components/DataExport'

export default function Account() {
  const nav = useNavigate()
  const { profile, isSuperadmin, isPlatformOnly, signOut, tier } = useSession()
  const [mode, setMode] = useTheme()

  if (!profile) return <Shell tabs />

  return (
    <Shell tabs>
      <button className="backlink" onClick={() => nav('/me')}>‹ Profile</button>
      <h2 className="q">Account</h2>
      <p className="sub">Security, data, and legal settings.</p>

      {/* Signing in, two-factor, devices — each renders its own head + group */}
      <Security />
      <Mfa />
      <Devices />

      <h3 className="sechead">Appearance</h3>
      <div className="rows">
        <div className="setting">
          <span>Dark mode<small>Auto follows Sydney time unless you override</small></span>
          <div className="segmented" role="group" aria-label="Appearance">
            {[['auto', 'Auto'], ['day', 'Day'], ['night', 'Night']].map(([m, label]) => (
              <button key={m} type="button" onClick={() => setMode(m)}
                      className={mode === m ? 'is-on' : undefined}
                      aria-pressed={mode === m}>{label}</button>
            ))}
          </div>
        </div>
      </div>

      {/* Catalog, licences and plan are a writer's, not staff's. */}
      {!isPlatformOnly && (
        <>
      <h3 className="sechead">Your songs and rights</h3>
      <div className="rows">
        <button className="action" onClick={() => nav('/catalog')}>
          <span>My catalog<small>Hashed, versioned publisher documents</small></span>
          <span className="ar">›</span>
        </button>
        <button className="action" onClick={() => nav('/licences')}>
          <span>Licences<small>Which songs are licensed to which church, and when</small></span>
          <span className="ar">›</span>
        </button>
        <button className="action" onClick={() => nav('/plans')}>
          <span>Plan<small>{tier === 'plus' ? 'Plus' : 'Free'} — Collective reach, covers, publishing help</small></span>
          <span className="ar">›</span>
        </button>
      </div>
        </>
      )}

      <h3 className="sechead">Support and legal</h3>
      <div className="rows">
        <button className="action" onClick={() => nav('/support/issue')}>
          <span>Report an issue<small>Tell us what's broken</small></span>
          <span className="ar">›</span>
        </button>
        <button className="action" onClick={() => nav('/support/feature')}>
          <span>Suggest a feature<small>Something you wanted and couldn't do</small></span>
          <span className="ar">›</span>
        </button>
        <button className="action" onClick={() => nav('/legal/privacy')}>
          <span>Privacy<small>What is kept, and for how long</small></span>
          <span className="ar">›</span>
        </button>
        <button className="action" onClick={() => nav('/legal/terms')}>
          <span>Terms<small>The agreement you are under</small></span>
          <span className="ar">›</span>
        </button>
        {/* Not in the danger group below. Asking for a copy of your own record
            is an ordinary thing to want, and putting it next to deletion makes
            it read as a step towards leaving. */}
        <DataExport profile={profile} />
      </div>

      {/* Platform staff, not church staff. It is the one group on this screen
          that isn't about the person reading it, so it gets its own head
          rather than being a loose row bolted onto Support. */}
      {isSuperadmin && (
        <>
          <h3 className="sechead">Platform</h3>
          <div className="rows">
            <button className="action" onClick={() => nav('/admin/platform')}>
              <span>Business<small>Signups, money, storage and what needs you</small></span>
              <span className="ar">›</span>
            </button>
            <button className="action" onClick={() => nav('/admin/moderation')}>
              <span>Moderation<small>What the three moderators have decided</small></span>
              <span className="ar">›</span>
            </button>
            <button className="action" onClick={() => nav('/admin/feedback')}>
              <span>Support<small>What people have written in about</small></span>
              <span className="ar">›</span>
            </button>
            <button className="action" onClick={() => nav('/admin/requests')}>
              <span>Publishing requests<small>Registration and distribution asked for by writers</small></span>
              <span className="ar">›</span>
            </button>
          </div>
        </>
      )}

      {/* Destructive, at the end, in its own group so neither row can be
          mistaken for one of the settings above it. */}
      <h3 className="sechead">Account</h3>
      <div className="rows danger">
        <button className="action" onClick={async () => { await signOut(); nav('/') }}>
          <span>Log out<small>{profile.email}</small></span>
          <span className="ar">›</span>
        </button>
        <AccountDelete profile={profile} onDeleted={() => nav('/')} />
      </div>
    </Shell>
  )
}
